import React, { useState, useEffect } from 'react'

interface UserFeedImageGridProps {
  images: string[]
  maxImages?: number
  imageClassName?: string
}

const Modal = ({ open, onClose, children }: { open: boolean; onClose: () => void; children: React.ReactNode }) => {
  useEffect(() => {
    if (!open) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open, onClose])

  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={onClose}>
      <div className="relative w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
        <button className="absolute right-2 top-2 z-10 rounded-full bg-white px-2 py-1 text-black" onClick={onClose}>
          ✕
        </button>
        {children}
      </div>
    </div>
  )
}

export default function UserFeedImageGrid({ images, maxImages = 5, imageClassName = '' }: UserFeedImageGridProps) {
  const [isGalleryVisible, setIsGalleryVisible] = useState(false)
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)
  const displayImages = images.slice(0, maxImages)

  const handleImageClick = (index: number) => {
    setSelectedImageIndex(index)
    setIsGalleryVisible(true)
  }

  if (displayImages.length === 0) return null

  // Grid rendering logic
  let grid
  switch (displayImages.length) {
    case 1:
      grid = (
        <div className={`h-[230px] overflow-hidden`}>
          <img
            src={displayImages[0]}
            className={`size-full cursor-pointer object-cover ${imageClassName}`}
            alt=""
            loading="lazy"
            onClick={() => handleImageClick(0)}
          />
        </div>
      )
      break
    case 2:
      grid = (
        <div className="flex h-[220px] gap-1.5">
          {displayImages.map((src, i) => (
            <div key={src} className="flex-1 overflow-hidden">
              <img
                src={src}
                className={`size-full cursor-pointer object-cover ${imageClassName}`}
                alt=""
                loading="lazy"
                onClick={() => handleImageClick(i)}
              />
            </div>
          ))}
        </div>
      )
      break
    case 3:
      grid = (
        <div className="flex h-[295px] gap-1.5">
          <div className="flex-1 overflow-hidden">
            <img
              src={displayImages[0]}
              className={`size-full cursor-pointer object-cover ${imageClassName}`}
              alt=""
              loading="lazy"
              onClick={() => handleImageClick(0)}
            />
          </div>
          <div className="flex flex-1 flex-col gap-1.5">
            {displayImages.slice(1).map((src, i) => (
              <div key={src} className="h-[142px] flex-1 overflow-hidden">
                <img
                  src={src}
                  className={`size-full cursor-pointer object-cover ${imageClassName}`}
                  alt=""
                  loading="lazy"
                  onClick={() => handleImageClick(i + 1)}
                />
              </div>
            ))}
          </div>
        </div>
      )
      break
    case 4:
      grid = (
        <div className="flex h-[380px] flex-col gap-1.5">
          {[0, 1].map((row) => (
            <div key={row} className="flex h-[190px] flex-row gap-1.5">
              {displayImages.slice(row * 2, (row + 1) * 2).map((src, i) => (
                <div key={src} className="flex-1 overflow-hidden">
                  <img
                    src={src}
                    className={`size-full cursor-pointer object-cover ${imageClassName}`}
                    alt=""
                    loading="lazy"
                    onClick={() => handleImageClick(row * 2 + i)}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
      )
      break
    case 5:
      grid = (
        <div className="flex h-[322px] flex-col gap-1.5">
          <div className="flex h-[200px] flex-row gap-1.5">
            {displayImages.slice(0, 2).map((src, i) => (
              <div key={src} className="flex-1 overflow-hidden">
                <img
                  src={src}
                  className={`size-full cursor-pointer object-cover ${imageClassName}`}
                  alt=""
                  loading="lazy"
                  onClick={() => handleImageClick(i)}
                />
              </div>
            ))}
          </div>
          <div className="flex h-[122px] flex-row gap-1.5">
            {displayImages.slice(2).map((src, i) => (
              <div key={src} className="flex-1 overflow-hidden">
                <img
                  src={src}
                  className={`size-full cursor-pointer object-cover ${imageClassName}`}
                  alt=""
                  loading="lazy"
                  onClick={() => handleImageClick(i + 2)}
                />
              </div>
            ))}
          </div>
        </div>
      )
      break
    default:
      grid = null
  }

  return (
    <>
      {grid}
      <Modal open={isGalleryVisible} onClose={() => setIsGalleryVisible(false)}>
        <img src={images[selectedImageIndex]} className="max-h-[80vh] max-w-full rounded-2xl object-contain" alt="" />
      </Modal>
    </>
  )
}
